export const EMPTY_PECUARIA = Object.freeze({ properties: [], plots: [], pastures: [], lots: [], occupations: [], measurements: [], sales: [] });
const PECUARIA_CROPS = new Set(['braquiaria', 'capim mombaca']);

const normalizeCrop = value => String(value || '').trim().toLocaleLowerCase('pt-BR').normalize('NFD').replace(/[\u0300-\u036f]/g, '');

export function isForageCrop(crop) {
  return PECUARIA_CROPS.has(normalizeCrop(crop));
}

export function cropAvailableForPecuaria(crop, status = 'ativo') {
  return ['ativo', 'planejado'].includes(status) && isForageCrop(crop);
}

export function pecuariaPlanting(plot) {
  const plantings = plot?.plantios?.length ? plot.plantios : (plot?.ciclo_atual ? [plot.ciclo_atual] : []);
  return plantings.find(planting => cropAvailableForPecuaria(planting.cultura, planting.status)) || null;
}

export function plotAvailableForPecuaria(plot, data = EMPTY_PECUARIA) {
  return Boolean(pecuariaPlanting(plot) || data.pastures.some(pasture => sameId(pasture.talhao_fisico_id, plot?.id) && !pasture.fim));
}

export const PASTURE_STATES = {
  occupied: { label: 'Ocupado', color: '#0c8c7d' },
  resting: { label: 'Em descanso', color: '#397ac2' },
  available: { label: 'Sem ocupação', color: '#668851' },
  blocked: { label: 'Indisponível', color: '#b65b40' },
  unconfigured: { label: 'Não configurado', color: '#738394' },
};
export const sameId = (a, b) => a != null && b != null && String(a) === String(b);
export const numberOrNull = value => value !== '' && value != null && Number.isFinite(Number(value)) ? Number(value) : null;
export function animalUnits(count, averageWeight, referenceWeight = 450) {
  const values = [count, averageWeight, referenceWeight].map(numberOrNull);
  return values.some(value => value == null || value <= 0) ? null : values[0] * values[1] / values[2];
}
export function elapsedDays(start, end = Date.now()) {
  const elapsed = new Date(end).getTime() - new Date(start).getTime();
  return !start || !Number.isFinite(elapsed) || elapsed < 0 ? null : Math.floor(elapsed / 86400000);
}
export function pastureState(plotId, data = EMPTY_PECUARIA, now = Date.now()) {
  const pasture = data.pastures.find(p => sameId(p.talhao_fisico_id, plotId) && !p.fim);
  const occupations = data.occupations.filter(o => sameId(o.talhao_fisico_id, plotId));
  const current = occupations.filter(o => !o.saiu_em);
  const lastExit = occupations.filter(o => o.saiu_em).map(o => o.saiu_em).sort((a, b) => new Date(b) - new Date(a))[0];
  const measurement = data.measurements.filter(m => sameId(m.talhao_fisico_id, plotId)).sort((a, b) => new Date(b.medido_em) - new Date(a.medido_em))[0];
  const state = !pasture ? 'unconfigured' : pasture.indisponivel ? 'blocked' : current.length ? 'occupied' : lastExit ? 'resting' : 'available';
  const ua = current.length ? current.reduce((total, o) => total + (animalUnits(o.quantidade, o.peso_medio_kg, o.referencia_ua_kg) || 0), 0) : 0;
  // Ocupações usam a área congelada na entrada, não a geometria mutável atual.
  const area = current[0]?.area_util ?? pasture?.area_util;
  return { ...PASTURE_STATES[state], state, pasture, current, measurement, lastExit,
    restDays: current.length ? null : elapsedDays(lastExit, now),
    occupationDays: current.length ? elapsedDays(current.map(o => o.entrou_em).sort()[0], now) : null,
    ua, uaPerHa: Number(area) > 0 ? ua / Number(area) : null };
}

export function pecuariaMapIndicator(plot, data = EMPTY_PECUARIA) {
  const linkedPhysicalPlot = data.plots.find(item => sameId(item.talhao_legado_id, plot?.id));
  const physicalId = plot?._physicalPlotId ?? (plot?._operationalPhysical ? plot.id : linkedPhysicalPlot?.id ?? null);
  const physicalPlot = physicalId == null ? null : data.plots.find(item => sameId(item.id, physicalId));
  const state = pastureState(physicalId, data);
  if (state.current.length) {
    const quantity = state.current.reduce((total, occupation) => total + (Number(occupation.quantidade) || 0), 0);
    return {
      type: 'livestock',
      label: state.current.map(occupation => occupation.nome_lote).filter(Boolean).join(' + ') || 'Lote no piquete',
      detail: `${quantity || '—'} animais · ${state.label}`,
      color: state.color,
      state: state.state,
    };
  }
  if (state.pasture) {
    return {
      type: 'pasture',
      label: state.pasture.especie || 'Pastagem',
      detail: state.restDays == null ? state.label : `${state.restDays} dias de descanso`,
      color: state.color,
      state: state.state,
    };
  }
  const plantings = physicalPlot?.plantios?.length
    ? physicalPlot.plantios
    : plot?.plantios?.length ? plot.plantios : (plot?.ciclo_atual ? [plot.ciclo_atual] : []);
  const planting = plantings.find(item => ['ativo', 'planejado'].includes(item?.status)) || null;
  return planting ? {
    type: 'crop',
    label: planting.cultura || 'Cultivo',
    detail: planting.status === 'planejado' ? 'Plantio planejado' : 'Cultivo ativo',
    color: '#b7791f',
    state: planting.status,
  } : null;
}
export const formatNumber = (value, digits = 2) => numberOrNull(value) == null ? '—' : Number(value).toLocaleString('pt-BR', { maximumFractionDigits: digits });
export const formatTime = value => value && Number.isFinite(new Date(value).getTime())
  ? new Date(value).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : 'Não informado';
export function localDateTime(value = new Date()) {
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}
export function toUtc(value) {
  const date = new Date(value);
  if (!value || !Number.isFinite(date.getTime())) throw new Error('Informe a data e a hora local do registro.');
  if (date.getTime() > Date.now() + 60000) throw new Error('Não é possível registrar uma movimentação futura.');
  return date.toISOString();
}

// randomUUID exige contexto seguro; getRandomValues também funciona no acesso
// local pela rede. Nunca usar Math.random para chaves de idempotência.
export function newRequestKey(cryptoProvider = globalThis.crypto || (typeof window !== 'undefined' ? window.crypto : undefined)) {
  const secureCrypto = cryptoProvider;
  if (typeof secureCrypto?.randomUUID === 'function') {
    const generated = secureCrypto.randomUUID();
    if (typeof generated === 'string' && generated) return generated;
  }
  if (typeof secureCrypto?.getRandomValues !== 'function') throw new Error('Navegador sem suporte seguro. Atualize-o para registrar a operação.');
  const bytes = secureCrypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
